import { useMemo, useState } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider';
import { useTheme } from '../../hooks/useTheme';
import { COLORS, FONTS } from '../../styles/tokens';
import type { ColorTheme } from '../../styles/tokens';
import {
  updateClient, enableClient, disableClient, deleteClient,
  fetchClientSecret,
} from '../../api/keycloak-admin';
import { uploadAppIcon } from '../../lib/deploy-api';
import { AppIcon } from '../../components/AppIcon';
import { IconUploader } from '../../components/app/IconUploader';
import { normalizeAppUrl } from './app-management-helpers';
import type { AppDetailContext } from './app-detail-context';

/** App settings sub-page: edit config, branding, secrets, enable/disable, delete */
export default function AppSettings(): React.JSX.Element {
  const { client, reload } = useOutletContext<AppDetailContext>();
  const { token } = useAuth();
  const { isDark } = useTheme();
  const colors = isDark ? COLORS.dark : COLORS.light;
  const navigate = useNavigate();

  const isProtected = client.clientId === 'vertex-hub';
  const attrs = client.attributes ?? {};

  const storedIcon = attrs['vertex.icon'] ?? '';
  const storedIconDark = attrs['vertex.icon.dark'] ?? '';
  const storedUris = (client.redirectUris ?? []).join(', ');

  const [name, setName] = useState(client.name || '');
  const [description, setDescription] = useState(client.description || '');
  const [team, setTeam] = useState(attrs['vertex.team'] ?? '');
  const [category, setCategory] = useState(attrs['vertex.category'] ?? '');
  const [hidden, setHidden] = useState((attrs['vertex.hidden'] ?? '') === 'true');
  const [iconFile, setIconFile] = useState<File | null>(null);
  const [iconDarkFile, setIconDarkFile] = useState<File | null>(null);
  const [redirectUris, setRedirectUris] = useState(storedUris);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [showDelete, setShowDelete] = useState(false);
  const [copiedSecret, setCopiedSecret] = useState(false);

  /** Save is pointless until something actually changed. */
  const isDirty = useMemo(() => (
    name !== (client.name || '')
    || description !== (client.description || '')
    || team !== (attrs['vertex.team'] ?? '')
    || category !== (attrs['vertex.category'] ?? '')
    || hidden !== ((attrs['vertex.hidden'] ?? '') === 'true')
    || redirectUris !== storedUris
    || iconFile !== null
    || iconDarkFile !== null
  ), [name, description, team, category, hidden, redirectUris, iconFile, iconDarkFile,
      client.name, client.description, attrs, storedUris]);

  /**
   * Redirect URIs, split and validated. `webOrigins` previously ran
   * `new URL(u).origin` directly over user input, so a single malformed entry
   * threw before the request was ever sent and surfaced as a raw parser error.
   */
  const uriCheck = useMemo(() => {
    const parts = redirectUris.split(',').map(u => u.trim()).filter(Boolean);
    const invalid = parts.filter(u => !normalizeAppUrl(u.replace(/\/\*$/, '')));
    return { parts, invalid };
  }, [redirectUris]);

  const handleSave = async () => {
    if (uriCheck.invalid.length > 0) {
      setError(`Not a valid URL: ${uriCheck.invalid.join(', ')}`);
      return;
    }
    try {
      setSaving(true);
      setError(null);

      // Upload any newly-chosen marks first; keep the stored ones otherwise.
      let iconUrl = storedIcon;
      if (iconFile) {
        iconUrl = await uploadAppIcon(token, client.clientId, iconFile);
      }
      let iconDarkUrl = storedIconDark;
      if (iconDarkFile) {
        iconDarkUrl = await uploadAppIcon(token, `${client.clientId}-dark`, iconDarkFile);
      }

      const origins = uriCheck.parts
        .map(u => normalizeAppUrl(u.replace(/\/\*$/, '')))
        .filter((u): u is string => u !== null)
        .map(u => new URL(u).origin);

      await updateClient(token, client.id, {
        name, description,
        redirectUris: uriCheck.parts,
        webOrigins: [...new Set(origins)],
        attributes: {
          ...attrs,
          'vertex.team': team,
          'vertex.category': category,
          'vertex.hidden': hidden ? 'true' : 'false',
          'vertex.icon': iconUrl,
          'vertex.icon.dark': iconDarkUrl,
        },
      });
      setIconFile(null);
      setIconDarkFile(null);
      await reload();
      setSuccess('Settings saved');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async () => {
    try {
      if (client.enabled) {
        await disableClient(token, client.id);
      } else {
        await enableClient(token, client.id);
      }
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const handleCopySecret = async () => {
    try {
      const secret = await fetchClientSecret(token, client.id);
      await navigator.clipboard.writeText(secret);
      setCopiedSecret(true);
      setTimeout(() => setCopiedSecret(false), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const handleDelete = async () => {
    if (deleteConfirmText !== client.clientId) return;
    try {
      await deleteClient(token, client.id);
      navigate('/admin/apps', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const inputStyle: React.CSSProperties = {
    height: '36px', padding: '0 12px', borderRadius: '6px', width: '100%',
    border: `1px solid ${colors.border}`,
    backgroundColor: isDark ? COLORS.dark.cardAlt : COLORS.light.cardAlt,
    color: colors.text, fontFamily: FONTS.body, fontSize: '13px', outline: 'none',
    boxSizing: 'border-box',
  };

  const labelStyle: React.CSSProperties = {
    fontFamily: FONTS.heading, fontSize: '9px', letterSpacing: '0.12em',
    textTransform: 'uppercase', color: colors.textSecondary, marginBottom: '4px',
  };

  const btnStyle: React.CSSProperties = {
    height: '34px', padding: '0 16px', borderRadius: '6px', border: 'none',
    background: isDark ? 'linear-gradient(90deg, #0266C6, #00407D)' : 'linear-gradient(90deg, #01A9ED, #0165C6)',
    color: '#FFF', fontFamily: FONTS.heading, fontSize: '11px', letterSpacing: '0.15em',
    textTransform: 'uppercase', cursor: 'pointer',
  };

  const ghostBtnStyle: React.CSSProperties = {
    ...btnStyle, background: 'transparent',
    border: `1px solid ${colors.border}`, color: colors.textSecondary,
  };

  const uploaderColors = {
    text: colors.text, textSecondary: colors.textSecondary,
    border: colors.border, card: colors.card,
  };

  if (isProtected) {
    return (
      <div style={{ padding: '40px 0', textAlign: 'center' }}>
        <p style={{ fontFamily: FONTS.heading, fontSize: '14px', color: colors.text }}>Protected App</p>
        <p style={{ fontFamily: FONTS.body, fontSize: '13px', color: colors.textSecondary, marginTop: '8px' }}>
          The Vertex Hub client cannot be modified.
        </p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '720px', paddingBottom: '32px' }}>
      {error && <StatusMsg text={error} type="error" isDark={isDark} />}
      {success && <StatusMsg text={success} type="success" isDark={isDark} />}

      <Section title="General" colors={colors} isDark={isDark}>
        {/* Short, related fields pair up; long values keep the full width. */}
        <Field label="Name" value={name} onChange={setName} inputStyle={inputStyle} labelStyle={labelStyle} />
        <Field label="Description" value={description} onChange={setDescription} inputStyle={inputStyle} labelStyle={labelStyle} />
        <Row>
          <Field label="Team" value={team} onChange={setTeam} inputStyle={inputStyle} labelStyle={labelStyle} />
          <Field label="Category" value={category} onChange={setCategory} inputStyle={inputStyle} labelStyle={labelStyle} />
        </Row>
        <div>
          <Field
            label="Redirect URIs (comma separated)"
            value={redirectUris}
            onChange={setRedirectUris}
            inputStyle={{
              ...inputStyle,
              borderColor: uriCheck.invalid.length > 0 ? '#EF4444' : colors.border,
            }}
            labelStyle={labelStyle}
          />
          {uriCheck.invalid.length > 0 && (
            <p style={{ fontFamily: FONTS.body, fontSize: '11px', color: '#EF4444', margin: '4px 0 0' }}>
              Not a valid URL: {uriCheck.invalid.join(', ')}
            </p>
          )}
        </div>
      </Section>

      <Section
        title="Branding"
        colors={colors}
        isDark={isDark}
        note="The dark variant is optional. Without it the primary mark is shown on a light plate in dark mode."
      >
        <div>
          <div style={labelStyle}>Logo</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <AppIcon
              icon={storedIcon}
              name={client.name || client.clientId}
              isDark={false}
              size={40}
            />
            <IconUploader
              isDark={isDark}
              colors={uploaderColors}
              onChange={setIconFile}
              currentUrl={storedIcon || undefined}
              hint="Shown in light theme and wherever no dark variant exists."
            />
          </div>
        </div>
        <div>
          <div style={labelStyle}>Logo for dark theme</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <AppIcon
              icon={storedIcon}
              iconDark={storedIconDark || undefined}
              name={client.name || client.clientId}
              isDark
              size={40}
            />
            <IconUploader
              isDark={isDark}
              colors={uploaderColors}
              onChange={setIconDarkFile}
              currentUrl={storedIconDark || undefined}
              hint="Optional. A light-on-transparent mark for dark surfaces."
            />
          </div>
        </div>
      </Section>

      <Section title="Visibility" colors={colors} isDark={isDark}>
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: 'pointer' }}>
          <input type="checkbox" checked={hidden} onChange={e => setHidden(e.target.checked)} style={{ marginTop: '2px' }} />
          <span style={{ fontFamily: FONTS.body, fontSize: '12px', color: colors.textSecondary, lineHeight: 1.4 }}>
            Need-to-know (hidden). Only users granted access see this app. When
            unchecked, the app is listed to everyone as request-access.
          </span>
        </label>
      </Section>

      {/* One save action for everything above it, so there is no question about
          which section a click applies to. */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '12px',
        padding: '16px 0', marginBottom: '8px',
        borderTop: `1px solid ${colors.border}`,
      }}>
        <button
          onClick={handleSave}
          disabled={saving || !isDirty || uriCheck.invalid.length > 0}
          style={{
            ...btnStyle,
            opacity: saving || !isDirty || uriCheck.invalid.length > 0 ? 0.45 : 1,
            cursor: saving || !isDirty ? 'not-allowed' : 'pointer',
          }}
        >
          {saving ? 'Saving' : 'Save Changes'}
        </button>
        {isDirty && !saving && (
          <span style={{ fontFamily: FONTS.body, fontSize: '11px', color: colors.textSecondary }}>
            Unsaved changes
          </span>
        )}
      </div>

      <Section title="Status & Secrets" colors={colors} isDark={isDark}>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button onClick={handleToggle} style={{ ...btnStyle, background: client.enabled ? '#EF4444' : '#4ADE80' }}>
            {client.enabled ? 'Disable App' : 'Enable App'}
          </button>
          <button onClick={handleCopySecret} style={ghostBtnStyle}>
            {copiedSecret ? 'Copied' : 'Copy Secret'}
          </button>
        </div>
      </Section>

      <Section title="Danger Zone" colors={colors} isDark={isDark} danger>
        {!showDelete ? (
          <button onClick={() => setShowDelete(true)} style={{ ...btnStyle, background: '#EF4444' }}>
            Delete App
          </button>
        ) : (
          <div>
            <p style={{ fontFamily: FONTS.body, fontSize: '12px', color: '#EF4444', margin: '0 0 8px' }}>
              Type {client.clientId} to confirm deletion. This removes the client, its
              realm role, and its stored icons.
            </p>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                type="text" value={deleteConfirmText}
                onChange={e => setDeleteConfirmText(e.target.value)}
                placeholder={client.clientId}
                style={{ ...inputStyle, borderColor: '#EF4444' }}
              />
              <button
                onClick={handleDelete}
                disabled={deleteConfirmText !== client.clientId}
                style={{
                  ...btnStyle, background: '#EF4444', flexShrink: 0,
                  opacity: deleteConfirmText !== client.clientId ? 0.45 : 1,
                }}
              >
                Delete
              </button>
              <button
                onClick={() => { setShowDelete(false); setDeleteConfirmText(''); }}
                style={{ ...ghostBtnStyle, color: colors.text, flexShrink: 0 }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </Section>
    </div>
  );
}

/** Two equal columns that collapse on narrow viewports. */
function Row({ children }: { children: React.ReactNode }): React.JSX.Element {
  return (
    <div className="vx-form-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px' }}>
      {children}
    </div>
  );
}

/**
 * Titled group separated by a hairline rule. Sections carry the structure so the
 * form reads as distinct concerns rather than one long column of inputs.
 */
function Section({ title, colors, isDark, note, danger, children }: {
  title: string;
  colors: ColorTheme;
  isDark: boolean;
  note?: string;
  danger?: boolean;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <section style={{
      marginBottom: '24px', padding: '20px',
      borderRadius: '8px',
      border: `1px solid ${danger ? 'rgba(239,68,68,0.35)' : colors.border}`,
      backgroundColor: danger
        ? (isDark ? 'rgba(239,68,68,0.06)' : 'rgba(239,68,68,0.04)')
        : (isDark ? COLORS.dark.card : COLORS.light.card),
    }}>
      <h3 style={{
        fontFamily: FONTS.heading, fontSize: '10px', letterSpacing: '0.15em',
        textTransform: 'uppercase', color: danger ? '#EF4444' : colors.textSecondary,
        margin: '0 0 4px',
      }}>
        {title}
      </h3>
      {note && (
        <p style={{ fontFamily: FONTS.body, fontSize: '11px', color: colors.textSecondary, margin: '0 0 12px', lineHeight: 1.5 }}>
          {note}
        </p>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: note ? 0 : '12px' }}>
        {children}
      </div>
    </section>
  );
}

/** Text field */
function Field({ label, value, onChange, inputStyle, labelStyle }: {
  label: string; value: string; onChange: (v: string) => void;
  inputStyle: React.CSSProperties; labelStyle: React.CSSProperties;
}): React.JSX.Element {
  return (
    <div>
      <div style={labelStyle}>{label}</div>
      <input type="text" value={value} onChange={e => onChange(e.target.value)} style={inputStyle} />
    </div>
  );
}

/** Status message */
function StatusMsg({ text, type, isDark }: { text: string; type: 'error' | 'success'; isDark: boolean }): React.JSX.Element {
  const isError = type === 'error';
  return (
    <div style={{
      padding: '12px 16px', borderRadius: '8px', marginBottom: '16px',
      backgroundColor: isDark ? (isError ? '#3b1c1c' : '#1c3b1c') : (isError ? '#fde8e8' : '#e8fde8'),
      border: `1px solid ${isError ? '#EF4444' : '#4ADE80'}`,
      color: isError ? '#EF4444' : '#4ADE80',
      fontFamily: FONTS.body, fontSize: '13px',
    }}>
      {text}
    </div>
  );
}
